/**
 * Turning open surfaces into the tab strip's view-model.
 *
 * The store owns *what is open*; this owns *what that looks like and what you
 * are allowed to do with it*. Keeping them apart matters because t3code's store
 * has no gating at all, while Forge has real rules — Diff, Task, Agents,
 * Desktop and Simulator need a persisted server thread, and Design is paused.
 * Those rules live here, over the store, so a draft thread cannot acquire a
 * surface it has no way to support.
 */
import { basenameOfPath } from "../filePath";
import type { RightPanelSurface, RightPanelKind } from "../rightPanelSurface";
import { RIGHT_PANEL_KIND_DEFINITIONS, SERVER_THREAD_REQUIRED_REASON } from "./rightPanelKinds";

export interface RightPanelSurfaceTab {
  /** The surface id, so an instanced tab addresses one instance. */
  readonly id: string;
  readonly kind: RightPanelKind;
  readonly label: string;
  readonly description: string;
  readonly active: boolean;
  readonly disabled: boolean;
  readonly disabledReason: string | null;
  readonly statusLabel: string | null;
  readonly shortcutLabel: string | null;
  /** The tab can be renamed in place; its strip label is the user's name. */
  readonly renamable: boolean;
  /** False for surfaces the user cannot dismiss. Every surface is closable today. */
  readonly closable: boolean;
}

export interface RightPanelSurfaceTabsInput {
  readonly surfaces: readonly RightPanelSurface[];
  readonly activeSurfaceId: string | null;
  readonly isServerThread: boolean;
  readonly shortcuts?: Partial<Record<RightPanelKind, string | null>> | undefined;
  /**
   * Display names for instanced surfaces that cannot name themselves — a
   * browser session's page title. Files take their name from their path.
   */
  readonly resourceLabels?: Readonly<Record<string, string>> | undefined;
}

/**
 * A file tab is named by its basename, not its path: a strip of tabs reading
 * `apps/web/src/…` repeated is unreadable, and the full path is one hover away.
 */
function surfaceLabel(
  surface: RightPanelSurface,
  resourceLabels: Readonly<Record<string, string>>,
): string {
  // The user's name for the tab wins over every derived one. A rename is a
  // deliberate "this tab is called X to me", not a hint to be second-guessed
  // by a fresher page title.
  if (surface.label !== undefined) return surface.label;
  if (surface.kind === "file") {
    return basenameOfPath(surface.relativePath) || surface.relativePath;
  }
  if (surface.kind === "preview") {
    return resourceLabels[surface.id] ?? (surface.resourceId === null ? "New tab" : "Browser");
  }
  if (surface.kind === "pull-request") {
    return surface.title?.trim() || `${surface.repository} #${surface.number}`;
  }
  return RIGHT_PANEL_KIND_DEFINITIONS[surface.kind].label;
}

function surfaceDescription(surface: RightPanelSurface): string {
  if (surface.kind === "file") return surface.relativePath;
  if (surface.kind === "pull-request") {
    return `${surface.host}/${surface.repository}#${surface.number}`;
  }
  return RIGHT_PANEL_KIND_DEFINITIONS[surface.kind].description;
}

/**
 * Maps a thread's surfaces to the tab model the strip renders: label,
 * description, shortcut, and the disabled or paused reasons each tab shows.
 */
export function buildRightPanelSurfaceTabs(
  input: RightPanelSurfaceTabsInput,
): readonly RightPanelSurfaceTab[] {
  const resourceLabels = input.resourceLabels ?? {};
  const shortcuts = input.shortcuts ?? {};

  return input.surfaces.map((surface) => {
    const definition = RIGHT_PANEL_KIND_DEFINITIONS[surface.kind];
    const blockedByDraftThread = definition.requiresServerThread && !input.isServerThread;
    // Order matters: a permanently paused surface stays paused on a server
    // thread, so its own reason wins over the draft-thread one.
    const pausedReason = definition.disabled === true ? (definition.disabledReason ?? null) : null;

    return {
      id: surface.id,
      kind: surface.kind,
      label: surfaceLabel(surface, resourceLabels),
      description: surfaceDescription(surface),
      active: input.activeSurfaceId === surface.id,
      disabled: definition.disabled === true || blockedByDraftThread,
      disabledReason: pausedReason ?? (blockedByDraftThread ? SERVER_THREAD_REQUIRED_REASON : null),
      statusLabel: definition.statusLabel ?? null,
      shortcutLabel: shortcuts[surface.kind] ?? null,
      renamable: definition.renamable === true,
      closable: true,
    };
  });
}

export interface AddableRightPanelKind {
  readonly kind: RightPanelKind;
  readonly label: string;
  readonly description: string;
  readonly disabled: boolean;
  readonly disabledReason: string | null;
  readonly statusLabel: string | null;
  readonly shortcutLabel: string | null;
}

/**
 * What the plus menu offers.
 *
 * A singleton kind already open is omitted — selecting it would activate the
 * existing tab, which the strip already does. Instanced kinds stay offered
 * however many are open, because "open another browser tab" is a real thing to
 * want and the old model's inability to express it is what this port fixes.
 *
 * `file` is never offered: you open a file by naming one, not by asking for an
 * empty file surface.
 */
export function buildAddableRightPanelKinds(input: {
  readonly surfaces: readonly RightPanelSurface[];
  readonly isServerThread: boolean;
  readonly shortcuts?: Partial<Record<RightPanelKind, string | null>> | undefined;
}): readonly AddableRightPanelKind[] {
  const shortcuts = input.shortcuts ?? {};
  const openKinds = new Set(input.surfaces.map((surface) => surface.kind));

  return Object.values(RIGHT_PANEL_KIND_DEFINITIONS)
    .filter((definition) => {
      if (definition.kind === "file" || definition.kind === "pull-request") return false;
      if (definition.hidden) return false;
      return definition.instanced || !openKinds.has(definition.kind);
    })
    .map((definition) => {
      const blockedByDraftThread = definition.requiresServerThread && !input.isServerThread;
      const pausedReason =
        definition.disabled === true ? (definition.disabledReason ?? null) : null;
      return {
        kind: definition.kind,
        label: definition.label,
        description: definition.description,
        disabled: definition.disabled === true || blockedByDraftThread,
        disabledReason:
          pausedReason ?? (blockedByDraftThread ? SERVER_THREAD_REQUIRED_REASON : null),
        statusLabel: definition.statusLabel ?? null,
        shortcutLabel: shortcuts[definition.kind] ?? null,
      };
    });
}
